import { memo, useRef } from "react";
import { ChevronLeft, ChevronRight, Radio } from "lucide-react";

const formatTimeAgo = (isoString, nowDate) => {
  if (!isoString) return "unknown";
  const seconds = Math.max(0, Math.round(((nowDate || new Date()).getTime() - new Date(isoString).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

// Strip a leading "CODE " prefix off the message WITHOUT a dynamic RegExp —
// codes like "????" are valid data but blow up `new RegExp("^????\\s*")`
// ("Nothing to repeat"), and an uncaught throw here unmounts the whole app.
const stripCode = (message = "", code = "") => {
  if (code && message.startsWith(code)) {
    return message.slice(code.length).replace(/^\s+/, "");
  }
  return message;
};

// Rows with a location are buttons that fly the map there; ones without stay
// plain text, so nothing looks clickable when there's nothing to click.
const ActivityRow = ({ event, now, onLocate }) => {
  const loc = event.location;
  const canLocate = !!(onLocate && loc && typeof loc.lat === "number");
  const body = (
    <>
      <span className="activity-code">{event.code}</span>
      <span className="activity-msg">{stripCode(event.message, event.code)}</span>
      <span className="activity-time">{formatTimeAgo(event.timestamp, now)}</span>
    </>
  );
  if (!canLocate) {
    return (
      <div className="activity-row" data-testid={`activity-row-${event.code}`}>
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      className="activity-row locatable"
      data-testid={`activity-row-${event.code}`}
      onClick={() => onLocate(loc)}
      title="Show this on the map"
    >
      {body}
    </button>
  );
};

/**
 * Slide-out activity drawer anchored to the right edge.
 * A thin persistent tab hangs off the edge; clicking it slides the panel in.
 * Shows the last 10 X-coded events.
 */
const ActivityPanelBase = ({ activities, now, open, onToggle, onLocate }) => {
  const recent = (activities || []).slice(0, 10);

  // Swipe-right-to-close (mobile). The drawer is anchored to the right edge,
  // so a rightward drag toward that edge dismisses it.
  const touchStart = useRef(null);
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    touchStart.current = null;
    // Rightward, mostly-horizontal swipe past the threshold -> close.
    if (open && dx > 55 && Math.abs(dx) > Math.abs(dy)) {
      onToggle();
    }
  };

  return (
    <>
      <button
        className={`activity-drawer-tab ${open ? "open" : ""}`}
        onClick={onToggle}
        data-testid="activity-drawer-tab"
        title={open ? "Collapse activity" : "Show activity"}
      >
        <Radio size={12} />
        <span className="activity-drawer-tab-label">ACTIVITY</span>
        {open ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>

      {/* The drawer stays mounted and is slid off-screen, so `aria-hidden`
          alone left its rows reachable by Tab — focus would land on controls
          nobody can see (and now that locatable rows are buttons, on more of
          them). `inert` takes them out of the tab order and the a11y tree
          together, which is what aria-hidden on its own cannot do. */}
      <aside
        className={`activity-drawer ${open ? "open" : ""}`}
        data-testid="activity-panel"
        aria-hidden={!open}
        inert={!open}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="activity-drawer-header">
          <span>ACTIVITY · LAST 10</span>
          <span className="activity-drawer-count">{recent.length}</span>
        </div>
        <div className="activity-drawer-body">
          {recent.length === 0 ? (
            <div className="empty-state">No activity yet…</div>
          ) : (
            recent.map((e, i) => (
              <ActivityRow key={`${e.timestamp}-${i}`} event={e} now={now} onLocate={onLocate} />
            ))
          )}
        </div>
      </aside>
    </>
  );
};

export const ActivityPanel = memo(ActivityPanelBase);
